import { z } from 'zod';
/**
 * Briques communes aux DTO de l'API.
 *
 * Convention : les dates circulent en ISO-8601 UTC (`2026-09-03T14:05:00.000Z`) ;
 * la conversion `Europe/Paris` et le format `JJ/MM/AAAA HH:MM` sont faits par
 * l'interface avec les utilitaires de `dates/`.
 */
/** Identifiant MongoDB sérialisé (24 caractères hexadécimaux). */
export const objectIdSchema = z
    .string()
    .regex(/^[0-9a-f]{24}$/i, 'identifiant invalide');
/** Horodatage ISO-8601 en UTC. */
export const isoDateTimeSchema = z.iso.datetime();
/** Jour civil parisien `AAAA-MM-JJ` (filtres de période, dates Pennylane). */
export const isoDaySchema = z.iso.date();
/**
 * Booléen transmis en query string. **Ne pas utiliser `z.coerce.boolean()`** :
 * il évalue `Boolean(value)`, donc la chaîne `"false"` devient `true` et tout
 * filtre « désactivé » est silencieusement inversé. Ce schéma accepte un vrai
 * booléen (valeur en mémoire côté routeur) ou les chaînes `"true"/"false"/"1"/"0"`.
 */
export const queryBooleanSchema = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((value) => value === true || value === 'true' || value === '1');
/** Pagination : 50 lignes par page dans l'interface (`02` §0). */
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 200;
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export const sortDirectionSchema = z.enum(['asc', 'desc']);
export const sortQuerySchema = z.object({
    sortBy: z.string().min(1).optional(),
    sortDir: sortDirectionSchema.default('desc'),
});
/** Filtre de période sur un champ de date (paiement, génération, run…). */
export const dateRangeQuerySchema = z.object({
    from: isoDateTimeSchema.optional(),
    to: isoDateTimeSchema.optional(),
});
export function paginatedSchema(item) {
    return z.object({
        items: z.array(item),
        total: z.number().int().min(0),
        page: z.number().int().min(1),
        pageSize: z.number().int().min(1),
    });
}
/**
 * Forme unique des erreurs HTTP (`04` §6) : jamais de pile d'appel en
 * production, un message en français courant et un détail technique optionnel.
 */
export const errorResponseSchema = z.object({
    error: z.object({
        message: z.string(),
        detail: z.string().optional(),
    }),
});
/** Réponse d'une action manuelle sans corps métier (`02` §0 : toast de succès). */
export const actionResultSchema = z.object({
    ok: z.literal(true),
    message: z.string().optional(),
});
/** Montant décimal transporté en chaîne pour éviter toute perte de précision. */
export const decimalStringSchema = z
    .string()
    .regex(/^-?\d+(\.\d+)?$/, 'montant décimal invalide');
/** Référence courte vers un objet (lien dans le journal, les alertes, les listes). */
export const subjectRefSchema = z.object({
    type: z.enum(['order', 'file', 'run', 'invoice', 'store', 'system']),
    id: z.string(),
    label: z.string(),
});
/**
 * Préréglage de planification (`02` §6 et §7 : « préréglage ou cron »).
 *
 * Miroir de `apps/server/src/jobs/cron.ts::SchedulePreset` : le serveur
 * convertit ce préréglage en expression cron via `presetToCron` avant de
 * l'enregistrer — la base ne connaît jamais que la chaîne cron.
 */
export const schedulePresetSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('every-minutes'), minutes: z.number().int().min(1).max(59) }),
    z.object({ kind: z.literal('hourly'), minute: z.number().int().min(0).max(59) }),
    z.object({
        kind: z.literal('every-hours'),
        hours: z.number().int().min(1).max(23),
        startHour: z.number().int().min(0).max(23),
        endHour: z.number().int().min(0).max(23),
        minute: z.number().int().min(0).max(59),
    }),
    z.object({
        kind: z.literal('daily'),
        hour: z.number().int().min(0).max(23),
        minute: z.number().int().min(0).max(59),
    }),
    z.object({
        kind: z.literal('weekly'),
        weekdays: z.array(z.number().int().min(0).max(6)).min(1),
        hour: z.number().int().min(0).max(23),
        minute: z.number().int().min(0).max(59),
    }),
]);
/**
 * Corps de mise à jour d'une planification : soit une expression cron libre,
 * soit un préréglage — jamais aucun des deux (`02` §6, §7).
 */
export const scheduleInputSchema = z
    .object({
    schedule: z.string().min(1).optional(),
    preset: schedulePresetSchema.optional(),
})
    .refine((body) => body.schedule !== undefined || body.preset !== undefined, {
    message: 'schedule ou preset requis',
    path: ['schedule'],
});
/** Aperçu des prochaines exécutions d'une planification (`02` §6, §7). */
export const scheduleSummarySchema = z.object({
    schedule: z.string(),
    description: z.string(),
    nextRuns: z.array(isoDateTimeSchema),
});
/**
 * Étapes faites en réalité par la v1 PHP pendant la bascule, alors que la v2
 * les calculait en shadow ou en simulation (`04` §10) : la transmission à
 * Martelet et la remontée de l'expédition dans Shopify. L'interface affiche
 * un badge « V1 » à la place de « Simulé ».
 */
export const handledByV1Schema = z.object({
    transmission: z.boolean(),
    shipping: z.boolean(),
});
//# sourceMappingURL=common.js.map